import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Phone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/common/Button";
import { site } from "@/content/site";
import { ROUTES } from "@/config/routes";

interface FounderTrustPanelProps {
  image?: string;
  title?: string;
  copy?: string;
  ctaLabel?: string;
  ctaHref?: string;
  dark?: boolean;
  compact?: boolean;
  className?: string;
}

export function FounderTrustPanel({
  image = "/assets/brand/founder-contact-gesture.webp",
  title = "Founder-led guidance for every site visit.",
  copy = "Speak with the Swargaseema team for project availability, route guidance, documentation clarity, and the next practical step.",
  ctaLabel = "Book Free Site Visit",
  ctaHref = ROUTES.CONTACT_US,
  dark = false,
  compact = false,
  className = ""
}: FounderTrustPanelProps) {
  return (
    <article
      className={`flex flex-col overflow-hidden rounded-lg border ${dark
        ? "border-[var(--gold)]/30 bg-[rgba(255,253,247,0.06)] text-[var(--white)]"
        : "border-[var(--line)] bg-[var(--white)] text-[var(--ink)] shadow-[0_16px_40px_rgba(23,60,49,0.08)]"
        } ${className}`}
    >
      <div className={`flex-1 grid ${compact ? "md:grid-cols-[190px_minmax(0,1fr)]" : "md:grid-cols-[230px_minmax(0,1fr)]"}`}>
        <div className={`relative flex h-[300px] items-start justify-center overflow-hidden md:h-[360px] ${dark ? "bg-[var(--ink-2)]" : "bg-[var(--mist)]"} px-5 pt-5`}>
          <Image
            src={image}
            alt="Swargaseema founder"
            width={520}
            height={760}
            sizes="(max-width: 800px) 70vw, 260px"
            className="h-auto max-h-[460px] w-auto !max-w-none object-contain object-top"
          />
        </div>
        <div className={`flex min-w-0 flex-col justify-center ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"}`}>
          <p className={`mb-3 inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] ${dark ? "text-[var(--gold-strong)]" : "text-[var(--gold)]"}`}>
            <ShieldCheck size={15} /> Founder-led trust
          </p>
          <h2 className={`font-serif ${compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"} font-medium leading-tight ${dark ? "text-white" : "text-[var(--ink)]"}`}>
            {title}
          </h2>
          <p className={`mt-4 max-w-2xl text-sm leading-7 ${dark ? "text-white/75" : "text-[var(--muted)]"}`}>{copy}</p>
        </div>
      </div>
      <div className={`flex flex-wrap items-center justify-center gap-4 border-t ${dark ? "border-white/10" : "border-[var(--line)]"} bg-[var(--mist)]/30 p-4 sm:p-5`}>
        <Link href={ctaHref}>
          <Button variant={dark ? "primary" : "primary"} className="min-w-[200px]" type="button">
            {ctaLabel} <ArrowRight size={16} className="shrink-0" />
          </Button>
        </Link>
        <a
          href={`tel:${site.phone.replaceAll(" ", "")}`}
          className={`inline-flex min-h-12 min-w-[200px] items-center justify-center gap-2 rounded-lg px-6 text-sm font-black transition ${dark ? "border border-white/20 bg-transparent text-white hover:bg-white/10" : "border border-[var(--line)] bg-white text-[var(--ink)] hover:bg-[var(--mist)]"
            }`}
        >
          <Phone size={16} className="shrink-0" /> Call {site.phone}
        </a>
      </div>
    </article>
  );
}
